import React, { useRef } from "react";
import { createTheme, ThemeProvider, Button } from "@mui/material";
import KeyboardArrowLeftRoundedIcon from "@mui/icons-material/KeyboardArrowLeftRounded";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { PickersDay, PickersDayProps } from "@mui/x-date-pickers/PickersDay";
import { enUS } from "date-fns/locale";
import { isBefore, isSameDay, isAfter } from "date-fns";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import {
  formatDisplayDate,
  formatShortDate,
} from "../../../../common/utils/date";

/**
 * The two-click date-range calendar, with its title row and Clear action.
 *
 * Extracted from `labBuilder/DateFilterSelect` so the same range picker can sit
 * behind two different triggers: that component's bordered toolbar pill, and
 * History's in-header column filter (`ColumnDateFilterMenu`). Both open this
 * body, so the calendar behaviour — first click sets the start, second sets the
 * end and swaps if picked backwards, year/month views only move the cursor —
 * cannot drift between them.
 *
 * `navDate` is owned by the caller because it must be re-seeded every time the
 * popover opens: without that the calendar reopens on whatever month the user
 * last browsed to instead of the selected range.
 */

interface Props {
  title: string;
  startDate: Date | null;
  endDate: Date | null;
  onDateChange: (start: Date | null, end: Date | null) => void;
  navDate: Date | null;
  setNavDate: (date: Date | null) => void;
}

/** Range label for a trigger: "Jul 16 - Jul 20 2026", or one date. */
export const formatDateRangeLabel = (
  startDate: Date | null,
  endDate: Date | null
): string | null => {
  if (!startDate) return null;
  if (!endDate) return formatDisplayDate(startDate);
  // Same year: drop the year from the start.
  if (startDate.getFullYear() === endDate.getFullYear()) {
    return `${formatShortDate(startDate)} - ${formatDisplayDate(endDate)}`;
  }
  return `${formatDisplayDate(startDate)} - ${formatDisplayDate(endDate)}`;
};

const DateRangeCalendar: React.FC<Props> = ({
  title,
  startDate,
  endDate,
  onDateChange,
  navDate,
  setNavDate,
}) => {
  const theme: Theme = useTheme();
  const awaitingEnd = useRef(false);
  const currentView = useRef<"day" | "month" | "year">("day");

  const handleDayClick = (date: Date | null) => {
    if (!date) return;

    // Always move the navigation cursor to the picked date so the visible
    // month follows year/month selection (and day picks) — the calendar's
    // month is controlled via `value={navDate}`.
    setNavDate(date);

    if (currentView.current !== "day") {
      // Year/month navigation — cursor already moved above.
      return;
    }

    if (!awaitingEnd.current) {
      onDateChange(date, null);
      awaitingEnd.current = true;
    } else {
      awaitingEnd.current = false;
      if (startDate && isSameDay(date, startDate)) {
        // Clicked the same day — keep it as a single date.
        return;
      }
      if (startDate && isBefore(date, startDate)) {
        onDateChange(date, startDate);
      } else {
        onDateChange(startDate, date);
      }
    }
  };

  const handleClear = () => {
    onDateChange(null, null);
    setNavDate(null);
    awaitingEnd.current = false;
  };

  const hasSelection = startDate !== null;

  const calendarTheme = createTheme({
    components: {
      // @ts-ignore
      MuiPickersCalendarHeader: {
        styleOverrides: {
          root: {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
            position: "relative" as "relative",
          },
          labelContainer: {
            position: "absolute" as "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            margin: 0,
            pointerEvents: "auto" as "auto",
            whiteSpace: "nowrap" as "nowrap",
          },
          label: {
            fontFamily: "var(--font-Bold)",
          },
        },
      },
      MuiPickersArrowSwitcher: {
        styleOverrides: {
          root: {
            position: "absolute" as "absolute",
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "space-between",
            pointerEvents: "none" as "none",
          },
          spacer: {
            display: "none",
          },
          button: {
            pointerEvents: "auto" as "auto",
          },
        },
      },
      MuiPickersDay: {
        styleOverrides: {
          root: {
            fontFamily: "var(--font-Medium)",
            fontSize: "12px",
            "&[aria-colindex='6']": {
              color: theme.colors_ds.PRIMARY_TEXT_MAIN,
            },
            "&[aria-colindex='7']": {
              color: theme.colors_ds.PRIMARY_TEXT_MAIN,
            },
            // Whiten only genuinely-picked days. Keying off `.Mui-selected`
            // (driven by CustomDay's `selected` prop) rather than
            // `[aria-selected]` — MUI marks today aria-selected just because it
            // matches the calendar's `value` (the nav cursor), which would leave
            // today's text white on a transparent (unfilled) background.
            "&.Mui-selected": {
              color: `${theme.colors_ds.NEUTRAL_GENERAL_WHITE} !important`,
              fontFamily: "var(--font-Bold)",
            },
          },
          today: {
            borderColor: "transparent",
          },
        },
      },
      MuiDayCalendar: {
        styleOverrides: {
          weekDayLabel: {
            flex: 1,
            fontFamily: "var(--font-Bold)",
            "&[aria-label='Saturday']": {
              color: theme.colors_ds.PRIMARY_TEXT_MAIN,
            },
            "&[aria-label='Sunday']": {
              color: theme.colors_ds.PRIMARY_TEXT_MAIN,
            },
          },
          weekContainer: {
            fontFamily: "var(--font-Bold)",
          },
        },
      },
      MuiPickersYear: {
        styleOverrides: {
          yearButton: {
            fontFamily: "var(--font-Medium)",
            fontSize: "12px",
            "&.Mui-selected": {
              fontFamily: "var(--font-Bold)",
              fontSize: "12px",
              borderRadius: "8px",
            },
            "&.Mui-disabled": {
              opacity: 0.6,
            },
            "&:hover": {
              borderRadius: "8px",
            },
          },
        },
      },
      MuiPickersMonth: {
        styleOverrides: {
          monthButton: {
            fontFamily: "var(--font-Medium)",
            fontSize: "12px",
            "&.Mui-selected": {
              fontFamily: "var(--font-Bold)",
              fontSize: "12px",
              borderRadius: "8px",
            },
            "&.Mui-disabled": {
              opacity: 0.6,
            },
            "&:hover": {
              borderRadius: "8px",
            },
          },
        },
      },
    },
  });

  const CustomDay = (props: PickersDayProps<Date>) => {
    const { day, ...other } = props;
    const hasRange = startDate && endDate && !isSameDay(startDate, endDate);
    const isStart = startDate && isSameDay(day, startDate);
    const isEnd = endDate && isSameDay(day, endDate);
    const isInRange =
      startDate && endDate && isAfter(day, startDate) && isBefore(day, endDate);

    const rangeBg = theme.colorTokens.P[20];

    let background = "transparent";
    if (hasRange) {
      if (isStart) {
        // Only highlight the right half (toward the range)
        background = `linear-gradient(to right, transparent 50%, ${rangeBg} 50%)`;
      } else if (isEnd) {
        // Only highlight the left half (toward the range)
        background = `linear-gradient(to left, transparent 50%, ${rangeBg} 50%)`;
      } else if (isInRange) {
        background = rangeBg;
      }
    }

    return (
      <div
        style={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          background,
        }}
      >
        <PickersDay
          {...other}
          day={day}
          selected={!!isStart || !!isEnd}
          disableMargin
          sx={
            isStart || isEnd
              ? { color: `${theme.colors_ds.NEUTRAL_GENERAL_WHITE} !important` }
              : undefined
          }
        />
      </div>
    );
  };

  return (
    <>
      <div style={styles.header}>
        <span style={styles.title(theme)}>{title}</span>
        <Button
          sx={{ ...styles.clearButton(theme), visibility: hasSelection ? "visible" : "hidden" }}
          onClick={handleClear}
        >
          Clear
        </Button>
      </div>
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={{ ...enUS, options: { weekStartsOn: 1 } }}
      >
        <ThemeProvider theme={calendarTheme}>
          <DateCalendar
            value={navDate}
            onChange={handleDayClick}
            onMonthChange={(month) => setNavDate(month)}
            onViewChange={(view) => {
              currentView.current = view;
            }}
            views={["year", "month", "day"]}
            openTo="day"
            slots={{
              day: CustomDay,
              leftArrowIcon: KeyboardArrowLeftRoundedIcon,
              rightArrowIcon: KeyboardArrowRightRoundedIcon,
              switchViewIcon: KeyboardArrowDownRoundedIcon,
            }}
            slotProps={{
              switchViewIcon: {
                sx: {
                  width: "24px",
                  height: "24px",
                  fontSize: "2rem",
                  padding: 0,
                  cursor: "pointer",
                  borderRadius: "50%",
                  color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
                },
              },
              previousIconButton: {
                sx: {
                  width: "32px",
                  height: "32px",
                  cursor: "pointer",
                  padding: "4px",
                  borderRadius: "50%",
                  color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
                  "&:hover": {
                    backgroundColor: theme.colorTokens.N[30],
                  },
                },
              },
              nextIconButton: {
                sx: {
                  width: "32px",
                  height: "32px",
                  cursor: "pointer",
                  padding: "4px",
                  borderRadius: "50%",
                  color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
                  "&:hover": {
                    backgroundColor: theme.colorTokens.N[30],
                  },
                },
              },
            }}
          />
        </ThemeProvider>
      </LocalizationProvider>
    </>
  );
};

export default DateRangeCalendar;

const styles = {
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 12px 8px 24px",
  },
  title: (theme: Theme) => ({
    fontFamily: "var(--font-Bold)",
    fontSize: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  clearButton: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.PRIMARY_TEXT_MAIN,
    textTransform: "none" as "none",
    padding: "2px 8px",
    minWidth: "auto",
  }),
};
